import { Star, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

const TRUSTPILOT_URL = 'https://www.trustpilot.com/review/www.britfiler.co.uk'
const DISMISS_KEY = 'britfiler-trustpilot-prompt'

/** Don't ask again for this long after the user answers either way. */
const SNOOZE_DAYS = { later: 30, reviewed: 365 }

type Outcome = 'later' | 'reviewed'

function isSuppressed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const until = Number(JSON.parse(raw)?.until)
    return Number.isFinite(until) && Date.now() < until
  } catch {
    return false
  }
}

function suppress(outcome: Outcome): void {
  try {
    const days = SNOOZE_DAYS[outcome]
    localStorage.setItem(
      DISMISS_KEY,
      JSON.stringify({ outcome, until: Date.now() + days * 24 * 60 * 60 * 1000 })
    )
  } catch {
    // A full or blocked localStorage shouldn't stop the user closing the modal.
  }
}

interface Props {
  /** Flip to true once a filing has actually succeeded. */
  open: boolean
  onClose: () => void
  /** e.g. "Confirmation Statement" — makes the ask specific to what was just filed. */
  filingLabel?: string
}

/**
 * Post-submission prompt inviting the user to review BritFiler on Trustpilot.
 *
 * Shown once a filing genuinely succeeds, then suppressed for a month (or a
 * year if they went to Trustpilot) so a regular filer isn't asked every time.
 */
export default function TrustpilotReviewModal({ open, onClose, filingLabel }: Props) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Re-check on each open: a user may have answered in another tab.
    if (open && !isSuppressed()) setVisible(true)
    if (!open) setVisible(false)
  }, [open])

  const close = (outcome: Outcome) => {
    suppress(outcome)
    setVisible(false)
    onClose()
  }

  if (!visible) return null

  const modal = (
    <div
      className='fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4'
      role='dialog'
      aria-modal='true'
      aria-labelledby='trustpilot-prompt-title'
    >
      <div className='w-full max-w-md rounded-2xl bg-white p-6 shadow-xl'>
        <div className='flex justify-end'>
          <button
            type='button'
            onClick={() => close('later')}
            aria-label='Close'
            className='rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='px-2 pb-2 text-center'>
          <div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50'>
            <div className='flex gap-0.5'>
              {[0, 1, 2].map(i => (
                <Star key={i} className='h-4 w-4 fill-green-600 text-green-600' />
              ))}
            </div>
          </div>

          <h2 id='trustpilot-prompt-title' className='text-lg font-bold text-gray-900'>
            {filingLabel ? `Your ${filingLabel} is filed` : 'Your filing is complete'}
          </h2>
          <p className='mt-2 text-sm leading-relaxed text-gray-600'>
            If BritFiler made that easier, would you tell others how it went? An honest review on
            Trustpilot helps other businesses decide whether we&apos;re right for them.
          </p>

          <div className='mt-6 space-y-2'>
            <a
              href={TRUSTPILOT_URL}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => close('reviewed')}
              className='flex w-full items-center justify-center gap-2 rounded-lg bg-[#00b67a] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#00a06c]'
            >
              <Star className='h-4 w-4 fill-white' />
              Review us on Trustpilot
            </a>
            <button
              type='button'
              onClick={() => close('later')}
              className='w-full rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900'
            >
              Maybe later
            </button>
          </div>

          <p className='mt-4 text-xs text-gray-400'>Opens Trustpilot in a new tab.</p>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
